import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Quote, Send, Sparkles, Radio } from "lucide-react";
import api from "../lib/api";
import { extractApiError } from "../lib/errors";

export default function Testimonies() {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [contact, setContact] = useState("");
  const [text, setText] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/testimonies/published");
      setItems(data);
    } catch {
      /* no-op */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please share your name.");
    if (text.trim().length < 20)
      return toast.error("Please share a little more of your story (20+ characters).");
    try {
      setLoading(true);
      await api.post("/testimonies", {
        name: name.trim(),
        location: location.trim(),
        contact: contact.trim(),
        testimony_text: text.trim(),
        consent_to_broadcast: consent,
      });
      toast.success("Thank you — your testimony has been received with thanksgiving.");
      setName("");
      setLocation("");
      setContact("");
      setText("");
      setConsent(false);
      load();
    } catch (er) {
      toast.error(extractApiError(er, "Could not submit testimony."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="testimonies-page" className="bg-canvas">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 -right-32 w-[40rem] h-[40rem] rounded-full opacity-25 blur-[120px]"
          style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-12">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
            Testimonies
          </span>
          <h1 className="mt-4 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            Stories of grace. Voices of hope.
          </h1>
          <p className="mt-5 text-[#C5D1EB] max-w-2xl leading-relaxed">
            Real stories from listeners and church family across New Zealand
            and beyond. Share yours — God's work in your life may be the
            encouragement someone else is praying for.
          </p>
        </div>
      </section>

      {/* Submit + featured */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-12 gap-6">
        <form
          onSubmit={submit}
          data-testid="testimony-form"
          className="lg:col-span-7 bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-10"
        >
          <div className="flex items-center gap-3">
            <Sparkles size={20} className="text-[#C5001A]" />
            <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight">
              Share your testimony
            </h2>
          </div>
          <p className="mt-2 text-sm text-[#C5D1EB] leading-relaxed">
            Briefly describe what God has done — healing, salvation, restored
            relationships, breakthroughs in faith, anything He has spoken to you.
          </p>

          <div className="mt-7 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                Name
              </label>
              <input
                data-testid="testimony-name"
                className="brand-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                Location
              </label>
              <input
                data-testid="testimony-location"
                className="brand-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, Country"
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Contact (email or phone) — optional
            </label>
            <input
              data-testid="testimony-contact"
              className="brand-input"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="We may reach out only if we want to feature your story."
            />
          </div>

          <div className="mt-5">
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Your testimony
            </label>
            <textarea
              data-testid="testimony-text"
              className="brand-input"
              rows={7}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tell us your story…"
            />
          </div>

          <label className="mt-5 flex items-start gap-3 text-[12px] text-[#C5D1EB]/85 leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              data-testid="testimony-consent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 accent-[#C5001A]"
            />
            <span>
              <span className="inline-flex items-center gap-1.5 text-[#FF3B30] font-medium">
                <Radio size={12} /> Broadcast consent
              </span>{" "}
              · I give Life Talk Radio permission to share or read this
              testimony on the radio broadcast or across the ministry's digital
              channels. I understand my name and city may be mentioned, and I
              consent to the ministry editing it for clarity.
            </span>
          </label>

          <button
            data-testid="testimony-submit"
            disabled={loading}
            className="mt-6 inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] disabled:opacity-60 text-white px-6 py-3 text-sm transition-colors"
          >
            <Send size={14} /> {loading ? "Sending…" : "Send my testimony"}
          </button>
        </form>

        <aside className="lg:col-span-5 flex flex-col gap-4" data-testid="featured-testimonies">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
            Featured
          </p>
          {items.length === 0 ? (
            <p className="text-sm text-[#C5D1EB]/60 bg-white/[0.03] backdrop-blur-md border border-white/10 p-6">
              No featured testimonies yet — yours could be the first.
            </p>
          ) : (
            items.map((t) => (
              <article
                key={t.id}
                className="bg-white/[0.04] backdrop-blur-md border border-white/10 p-6"
              >
                <Quote size={18} className="text-[#C5001A]" />
                <p className="mt-3 text-sm text-white leading-relaxed">
                  {t.featured_quote || t.testimony_text}
                </p>
                <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-[#C5D1EB]/70">
                  {t.name}
                  {t.location ? ` · ${t.location}` : ""}
                </p>
              </article>
            ))
          )}
        </aside>
      </section>
    </main>
  );
}
